/**
 * Auth URL utilities - Compute login/auth URLs
 *
 * Auth pages are now served natively by judge-web at /auth/login and /auth/register.
 * These utilities compute the correct origin and path.
 *
 * Examples:
 *   platform.testifysec.localhost/auth/login
 *   judge.aws-sandbox-staging.testifysec.dev/auth/login
 *   platform.testifysec.com/auth/login
 */

/**
 * Compute login URL base from current origin.
 * Auth pages are served by judge-web itself (same origin).
 */
export const getLoginBaseUrl = (): string => {
  if (typeof window === 'undefined') return '/auth/login'; // SSR fallback
  return `${window.location.origin}/auth/login`;
};

/**
 * Compute registration URL base from current origin.
 * Auth pages are served by judge-web itself (same origin).
 */
export const getRegistrationBaseUrl = (): string => {
  if (typeof window === 'undefined') return '/auth/register'; // SSR fallback
  return `${window.location.origin}/auth/register`;
};

/**
 * Get full login URL with return_to parameter and optional provider hint
 *
 * @param provider - Optional OAuth provider to hint for login (github, gitlab)
 * @param returnTo - Optional custom return URL (defaults to current page)
 */
export const getLoginUrl = (provider?: 'github' | 'gitlab', returnTo?: string): string => {
  if (typeof window === 'undefined') {
    return '/auth/login';
  }
  const returnUrl = returnTo ?? window.location.href;
  const encodedReturnTo = encodeURIComponent(returnUrl);
  const base = `${getLoginBaseUrl()}?return_to=${encodedReturnTo}`;
  if (provider) {
    return `${base}&provider=${provider}`;
  }
  return base;
};

/**
 * Get full registration URL with return_to parameter
 *
 * @param returnTo - Optional custom return URL (defaults to current page)
 */
export const getRegistrationUrl = (returnTo?: string): string => {
  if (typeof window === 'undefined') {
    return '/auth/register';
  }
  const returnUrl = returnTo ?? window.location.href;
  const encodedReturnTo = encodeURIComponent(returnUrl);
  return `${getRegistrationBaseUrl()}?return_to=${encodedReturnTo}`;
};

/**
 * Compute Kratos URL from current origin.
 * Kratos is now embedded in judge-api and served at /kratos path prefix
 * (same origin as the web app, no subdomain routing needed).
 */
export const getKratosUrl = (): string => {
  if (typeof window === 'undefined') return '/kratos'; // SSR fallback
  return `${window.location.origin}/kratos`;
};

// The Kratos client lives in src/shared/kratos/client.ts. Importing it here
// would re-introduce a module dependency only consumers actually need. Auth
// pages and useSessionManager import { kratos } from '@/shared/kratos/client'
// directly. See that file for the rationale on dropping @ory/client.